import React, { useCallback, useMemo, useState } from "react";

import Svg from "@/styles/utils/Svg";
import { isTouchDevice } from "@/utils/getters";

import { OptionEnum, OptionModeEnum, OptionTypeEnum } from "../index";
import {
  FadeIn,
  Image,
  ImageWrapper,
  Percent,
  Status,
  StatusIcon,
  StatusText,
  TextBox,
  VotedBar,
} from "./styles";

export type ImageTileProps = {
  mode: OptionModeEnum;
  type: OptionTypeEnum;
  answer?: OptionEnum;
  percent?: number;
  icons: IIcons;
  interactionId: string;
  slideId: string;
  optionId: string;
  imageUrl: string;
  description?: string;
  isClickable: boolean;
  isSecret?: boolean;
  onClick?: Function;
};

export const ImageOption: React.FC<ImageTileProps> = ({
  mode,
  type,
  answer,
  percent,
  icons,
  interactionId,
  slideId,
  optionId,
  imageUrl,
  description,
  isClickable,
  isSecret,
  onClick,
}) => {
  const [hovered, setHovered] = useState(false);
  const [wasClicked, setWasClicked] = useState(false);

  const isTouchDeviceValue = useMemo(() => isTouchDevice(), []);

  const handleClick = useCallback((): void => {
    if (!isClickable) {
      return;
    }

    if (onClick) {
      if (type === OptionTypeEnum.quiz) {
        onClick({
          quizId: interactionId,
          slideId,
          optionId,
        });
      } else {
        onClick({ interactionId, slideId, optionId });
      }
    }

    setWasClicked(true);
  }, [isClickable, onClick, type, interactionId, slideId, optionId]);

  const renderStatus = useCallback((): React.JSX.Element => {
    const isCorrectAnswer = answer && answer === OptionEnum.correct;
    const showAsActualSolution = !wasClicked && answer === OptionEnum.correct;
    const displayTextbox =
      (description &&
        (hovered || isTouchDeviceValue) &&
        !(!isClickable || mode === OptionModeEnum.results)) ||
      (isSecret && !isClickable && answer === OptionEnum.vote);

    if (type === OptionTypeEnum.quiz) {
      const innerText = (
        <React.Fragment>
          <TextBox $show={!!displayTextbox}>{description}</TextBox>
          <StatusIcon>
            <Svg
              contents={isCorrectAnswer ? icons.Success : icons.Fail}
              width={66}
              height={66}
            />
            <StatusText>
              {isCorrectAnswer ? "Richtig!" : "Leider falsch!"}
            </StatusText>
          </StatusIcon>
        </React.Fragment>
      );
      return (
        <React.Fragment>
          {answer && (
            <Status
              $type={type}
              $answer={answer}
              $withoutRipple={showAsActualSolution}
              $overlay={false}
            >
              {showAsActualSolution ? (
                <FadeIn $timing={800} $delay={300}>
                  {innerText}
                </FadeIn>
              ) : (
                innerText
              )}
            </Status>
          )}
        </React.Fragment>
      );
    } else {
      const hasResult = !isClickable || mode === OptionModeEnum.results;
      return (
        <React.Fragment>
          <Status
            $type={type}
            $answer={answer}
            $overlay={!isClickable && typeof percent === "number"}
          >
            <TextBox $show={!!displayTextbox}>{description}</TextBox>
            {!isSecret && hasResult && (
              <React.Fragment>
                <StatusIcon>
                  <Percent $answer={answer}>{percent}%</Percent>
                  {hasResult && (
                    <StatusText $lite={true}>{description}</StatusText>
                  )}
                </StatusIcon>
              </React.Fragment>
            )}
            {!isSecret && <VotedBar $percent={`${percent || 0}%`} />}
          </Status>
        </React.Fragment>
      );
    }
  }, [
    hovered,
    wasClicked,
    mode,
    type,
    icons,
    answer,
    description,
    percent,
    isClickable,
    isSecret,
    isTouchDeviceValue,
  ]);

  return (
    <ImageWrapper
      $mode={mode}
      $type={type}
      $answer={answer}
      $isClickable={isClickable}
      onClick={handleClick}
      onMouseOver={isTouchDeviceValue ? undefined : () => setHovered(true)}
      onMouseLeave={isTouchDeviceValue ? undefined : () => setHovered(false)}
    >
      <Image $answer={answer} $imageUrl={imageUrl} $isClickable={isClickable}>
        {renderStatus()}
      </Image>
    </ImageWrapper>
  );
};
